export const microservicesDesignModule = {
  id: "sd-microservices-design",
  title: "Microservices Architecture",
  description: "Master microservices design patterns, service boundaries, and distributed system challenges for enterprise applications.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Design effective microservice boundaries using domain-driven design principles
- Implement inter-service communication patterns that maintain system reliability
- Manage distributed data consistency and transaction challenges
- Handle service failures gracefully using proven resilience patterns

## Prerequisites
- Understanding of distributed systems concepts
- Experience with REST APIs and databases
- Knowledge of basic software architecture patterns

## Overview
Microservices architecture has become the standard for enterprise applications requiring scale, flexibility, and independent team deployment. However, most microservices initiatives fail due to poor service design and inadequate handling of distributed system complexity. This module provides proven patterns from leaders in the field for designing microservices that deliver business value while managing inherent distributed system challenges.

---

## Key Concepts

### Service Boundary Design Using Domain-Driven Design
Effective microservices reflect business domain boundaries rather than technical concerns.

Bounded Context: A logical boundary within which a domain model applies consistently.
Example e-commerce contexts:
- User Management: Profiles, authentication, preferences
- Catalog: Product information, categories, inventory
- Order: Shopping cart, checkout, order processing
- Payment: Payment methods, transactions, billing

Service Boundary Identification Techniques:
- Event storming workshops
- Data ownership analysis; services should own their data completely
- Team cognitive load; services should align with team capacity (typically 2-8 people)
- Business capability mapping; services should deliver complete business capabilities

### Inter-Service Communication Patterns
Microservices must communicate while maintaining independence and reliability.

Synchronous (HTTP or gRPC)
- Use for: Real-time queries, simple request-response
- Benefits: Simple, immediate response, easy debugging
- Drawbacks: Tight coupling, cascading failures, latency accumulation

Asynchronous (Message Queues or Events)
- Use for: Business events, batch processing, loose coupling
- Benefits: Resilience, scalability, temporal decoupling
- Drawbacks: Eventual consistency, complexity, harder debugging

Saga Pattern for Distributed Transactions
- Problem: No ACID transactions across services
- Solution: Choreographed or orchestrated sequence of local transactions
- Example choreography flow: Order created, payment processed, items reserved, order confirmed

### Data Management in Microservices
Each service must own its data while supporting business queries across services.

Database per Service Pattern
- Principle: Each microservice has its own database schema or instance
- Benefits: Independent deployment, technology choice, performance tuning
- Challenges: Cross-service queries, data consistency, reporting

Event Sourcing for Data Consistency
- Concept: Store events rather than only current state
- Benefits: Complete audit trail, temporal queries, replay capability
- Implementation: Each service publishes domain events to an event bus

---

## Practical Examples

Example 1: E-commerce Order Service Design
Service responsibilities include managing the order lifecycle (created to confirmed to shipped to delivered), coordinating with payment and inventory services, handling modifications and cancellations, and providing order history and tracking.

API sketch:
- POST /orders with customerId, items, and shippingAddress
- GET /orders/{orderId}
- PUT /orders/{orderId}/cancel
- GET /customers/{customerId}/orders

Events sketch:
- OrderCreated with orderId, customerId, items, totalAmount, timestamp
- OrderConfirmed with orderId, paymentId, estimatedDelivery
- OrderCancelled with orderId, reason, refundAmount
- OrderShipped with orderId, trackingNumber, carrier

Data model sketch:
- Orders: id, customerId, status, totalAmount, createdAt, updatedAt
- OrderItems: orderId, productId, quantity, price, productName
- OrderEvents: orderId, eventType, eventData, timestamp

Example 2: Handling Service Failure Scenarios
Resilience patterns to handle distributed system failures.

Circuit Breaker Pattern (pseudo-code):
- Create a client wrapper with a circuit breaker that opens after repeated failures
- While open, short-circuit calls and return fallback until reset timeout elapses

Retry with Exponential Backoff (pseudo-code):
- Attempt an operation up to N times
- On failure, wait 2^attempt seconds between retries
- If all retries fail, bubble up the error

Compensation Actions (Saga) (pseudo-code):
- On order creation, process payment and reserve inventory
- If any step fails, cancel order, refund payment if processed, and release reservation

Pro Tip: Start with a modular monolith and extract services based on actual team boundaries and deployment needs rather than theoretical service boundaries.

---

## Industry Adoption

Microservices Success Metrics
Organizations with successful implementations report faster time-to-market, improved availability, reduced deployment risk, and higher team productivity.

Popular Tools
- Service mesh for service-to-service communication
- API gateways for ingress traffic
- Message brokers for asynchronous communication
- Distributed tracing for observability

---

## Key Takeaways

- Design service boundaries around business domains, not technical layers
- Use asynchronous communication for business events and synchronous for queries
- Implement compensation patterns for distributed transaction management
- Plan for failure with circuit breakers, retries, and graceful degradation

Common Pitfalls
- Creating too many small services that increase operational complexity
- Ignoring data consistency challenges in distributed systems
- Under-investing in monitoring and observability infrastructure

## Next Steps
Next, you will learn enterprise integration patterns for connecting microservices with existing systems and external partners.

---
`,
  duration: "6 hours",
  difficulty: "advanced" as const,
  objectives: [
    "Design effective microservice boundaries",
    "Implement service communication patterns",
    "Manage distributed data consistency",
    "Handle service failures gracefully"
  ],
  keyTopics: [
    "Domain-Driven Design",
    "Service Communication",
    "Saga Pattern",
    "Circuit Breaker",
    "Event Sourcing"
  ]
};